

import { TAIForm, ISummary } from 'src/types/aiForm';
import { IAIARequest } from 'src/types/org/aia';
import { IOrgModel } from 'src/types/org/orgInterface';
import { Draft } from 'src/types/common';
import { TYPE_CODE_OTHER } from 'src/types/org/constants';
import { IAIUpdate } from 'src/types/org/updateRequest';
import { IAIRecord } from 'src/types/org/ai/aiInterface';
import { IAddress } from 'src/types';
import { IMsReferenceService, IRegion } from './msReference';


const aiDefaults = {
  aiStatusCd: 2,
  stateHomeSchoolInd: false,
  annualReviewInd: false,
  initialIngestionPathCd: 2,
  crossPrgSecHoldInd: false
};

export interface IAiService {
  mapAiUpdate: (data: IAIUpdate) => Promise<Draft<IAIRecord>>;
  mapAiCreate: (data: TAIForm) => Promise<Draft<IAIRecord>>;
  mapNewAiCreate: (data: any) => Promise<Draft<IAIRecord>>;
  mapAiaCreate: (data: IAIARequest) => Promise<Draft<IAIRecord>>;
  mapAiCreateHints: (form: TAIForm) => any;
  mapNewAiCreateHints: (form: any) => any;
}

const parseCode = (code: number | string): number | undefined => {
  if (typeof code === 'string') {
    const parsed = parseInt(code);
    return !isNaN(parsed) ? parsed : undefined;
  } else if (typeof code === 'number') {
    return code;
  } else {
    return undefined;
  }
};

const checkAddress = (address: any) => {
  const {
    address1,
    city,
    state,
    zip,
    stateOrProvince,
    country,
    internationalPostalCode
  } = address || {};

  if (address1 && city && (state || stateOrProvince) && country && (zip || internationalPostalCode)) {
    return true;
  }
  return false;
};

const getTypes = (form: TAIForm) => {
  const optionMap = {
    schoolTypePublic: "Public",
    schoolTypePrivateRel: "Private Religious",
    schoolTypeCatholic: "Catholic",
    schoolTypePrivate: "Private",
    schoolTypeYouth: "Youth",
    schoolTypeCharter: "Charter",
    schoolTypeHome: "Home",
    schoolTypeOnline: "Online",
    schoolTypeDefense: "Defense",
    schoolTypeIndianAffairs: "Indian Affairs"
  };

  const values = [];
  const types = (form.type || {});
  for (const key of Object.keys(types)) {
    if (key === 'schoolTypeOther') {
      const other = types['schoolTypeOtherExplanation'];
      if (other) {
        values.push(other);
      }
    } else if (types[key] === 'yes') {
      values.push(optionMap[key as keyof typeof optionMap]);
    }
  }
  return values;
};
/**
 * 
 * @param form 
 * This function maps values from school code create form's different values for school type 
 * to the values acceptable to operation form
 * @returns 
 */
const convertToOpsFormTypes = (form: any) => {
  // Maps from school code create form's JSON  values for different school types to operation form acceptable values
  const typesMap: { [key: string]: string } = {
    'Public': 'Public',
    'Private (religious)': 'Private Religious',
    'Private (no religious affiliation)': 'Private',
    'Catholic': 'Catholic',
    'Youth Correctional Facility': 'Youth',
    'Charter': 'Charter',
    'Department of Defense': 'Defense',
    'Bureau of Indian Affairs': 'Indian Affairs',
    'Home School Association': 'Home',
    'Course/Curriculum Provider': 'Curriculum Provider',
    'Course Delivery Primarily Online': 'Online'
  };

  const appForm = form?.data?.appForm || {};
  let typeValues = appForm.type || [];
  const values = [];
  
  for (let i = 0; i < typeValues.length; i++) {
    const typeValue = typeValues[i];
    
    if (typeValue === 'Other') {
      // Replace 'Other' with the actual typeOther value
      const otherValue = appForm.typeOther;
      if (otherValue) {
        values.push(otherValue);
      }
    } else {
      // Map  type from school code create form format to operation form format, or use original if not in map
      const mappedValue = typesMap[typeValue] || typeValue;
      values.push(mappedValue);
    }
  }
  
  return values;
};

const enrollmentFields = [
  {
    field: 'zeroEnrolled',
    grade: 0,
    key: 23
  },
  {
    field: 'oneEnrolled',
    grade: 1,
    key: 24
  },
  {
    field: 'twoEnrolled',
    grade: 2,
    key: 25
  },
  {
    field: 'threeEnrolled',
    grade: 3,
    key: 26
  },
  {
    field: 'fourEnrolled',
    grade: 4,
    key: 27
  },
  {
    field: 'fiveEnrolled',
    grade: 5,
    key: 28
  },
  {
    field: 'sixEnrolled',
    grade: 6,
    key: 14
  },
  {
    field: 'sevenEnrolled',
    grade: 7,
    key: 15
  },
  {
    field: 'eightEnrolled',
    grade: 8,
    key: 2
  },
  {
    field: 'nineEnrolled',
    grade: 9,
    key: 4
  },
  {
    field: 'tenEnrolled',
    grade: 10,
    key: 5
  },
  {
    field: 'elevenEnrolled',
    grade: 11,
    key: 6
  },
  {
    field: 'twelveEnrolled',
    grade: 12,
    key: 7
  }
];

export const AiService = (refService: IMsReferenceService): any => {
  const mapAddress = async (address: Draft<IAddress>) => {
    let region: IRegion | undefined;
    if (address.regionCd) {
      region = await refService.findProvince(address.regionCd);
    }

    if (!region && address.province) {
      region = await refService.findProvince(address?.province);
      address.regionCd = null;
    }

    if (region) {
      address.regionCd = region.key;
      address.province = region.label;
    }

    return address;
  };

  /**
   * Do not add keys when user does not submit those fieldss
   * @param data
   */
  const mapAiUpdate = async (data: IAIUpdate): Promise<Draft<IAIRecord>> => {
    const {
      name,
      visitingAddress,
      shippingAddress,
      email,
      website,
      lowestGradeCd,
      highestGradeCd,
      aiLevelCd,
      aiStatusCd,
      fax
    } = data; // user submission fields

    return Promise.resolve({
      org: {
        name,
        visitingAddress: await mapAddress(visitingAddress),
        shippingAddress: shippingAddress ? await mapAddress(shippingAddress) : undefined,
        email,
        phone: {},
        website,
        lowestGradeCd: parseCode(lowestGradeCd),
        highestGradeCd: parseCode(highestGradeCd),
        fax: fax?.formatted ? {} : undefined
      },
      ai: {
        aiLevelCd: parseCode(aiLevelCd),
        aiStatusCd: parseCode(aiStatusCd)
      }
    });

    // need to expand these mappers to include a full payload that includes an AI section
    // and need to add support for original records, either retrieving internally here
    // or passing into function, need to evalute use cases
  };

  const mapAiaCreate = async (data: IAIARequest): Promise<Draft<IAIRecord>> => {
    const { data: application } = data;
    if (!application)
      throw new Error("Application request data not found for submission.");

    const { aia } = application;
    if (!aia)
      throw new Error("AIA data not found in application data.");

    const {
      name,
      visitingAddress,
      shippingAddress,
      email,
      website,
    } = aia;

    const nameShort = (name) ? (name.length < 50 ? name : name.slice(0, 50)) : '';
    const nameAbbr = (name) ? (name.length < 30 ? name : name.slice(0, 30)) : '';

    return {
      org: {
        name,
        nameShort,
        nameAbbr,
        visitingAddress: await mapAddress(visitingAddress),
        shippingAddress: shippingAddress ? await mapAddress(shippingAddress) : undefined,
        email,
        phone: {},
        website,
        charterInd: false,
        campus: false,
        orgTypeCd: TYPE_CODE_OTHER
      },
      ai: {

      }
    };
  };

  const mapAiCreate = async (data: TAIForm): Promise<Draft<IAIRecord>> => {
    const {
      schoolName,
      visitingAddress,
      shippingAddress,
      schoolEmail,
      webSite,
    } = data;

    const nameShort = (schoolName) ? (schoolName.length < 50 ? schoolName : schoolName.slice(0, 50)) : '';
    const nameAbbr = (schoolName) ? (schoolName.length < 30 ? schoolName : schoolName.slice(0, 30)) : '';

    let minGrade: undefined | number = undefined;
    let maxGrade: undefined | number = undefined;

    enrollmentFields.forEach((map) => {
      const key = map.key;
      const field = map.field;
      const enrollment = data.studentEnrolled || {};
      const value = enrollment[field];
      const count = parseInt(value);
      if (!isNaN(count) && count > 0) {
        if (minGrade === undefined) {
          minGrade = key;
        } else {
          maxGrade = key;
        }
      }
    });

    if (maxGrade === undefined) {
      maxGrade = minGrade;
    }

    const mappedVisiting: Draft<IAddress> = {
      streetAddr1: visitingAddress?.address1,
      streetAddr2: visitingAddress?.address2 || undefined,
      streetAddr3: visitingAddress?.address3 || undefined,
      city: visitingAddress?.city,
      stateCd: visitingAddress?.state,
      zip5: visitingAddress?.zip,
      province: visitingAddress?.stateOrProvince || undefined,
      countryIsoCd: visitingAddress?.country,
      intlPostalCd: visitingAddress?.internationalPostalCode || undefined,
    };

    const mappedShipping: Draft<IAddress> | undefined = (shippingAddress && checkAddress(shippingAddress)) ? {
      streetAddr1: shippingAddress?.address1,
      streetAddr2: shippingAddress?.address2 || undefined,
      streetAddr3: shippingAddress?.address3 || undefined,
      city: shippingAddress?.city,
      stateCd: shippingAddress?.state,
      zip5: shippingAddress?.zip,
      province: shippingAddress?.stateOrProvince || undefined,
      countryIsoCd: shippingAddress?.country,
      intlPostalCd: shippingAddress?.internationalPostalCode || undefined,
      label: shippingAddress?.label || undefined,
      contactName: shippingAddress?.contactName || undefined,
      contactPhone: shippingAddress?.contactPhone || undefined
    } : undefined;

    return {
      org: {
        name: schoolName,
        nameShort: nameShort,
        nameAbbr: nameAbbr,
        email: schoolEmail,
        website: webSite,
        visitingAddress: await mapAddress(mappedVisiting),
        shippingAddress: mappedShipping ? await mapAddress(mappedShipping) : undefined,
        charterInd: false,
        campus: false,
        lowestGradeCd: minGrade,
        highestGradeCd: maxGrade,
        phone: {}
      },
      ai: {

      }
    };
  };

  /**
   * 
   * @param data 
   * 
   * This function maps the needed fields like name, visitingAddress, 
   * shippingAddress, email, and website from the new school code create form
   * to the format needed for operation form
   * 
   * @returns 
   */
  const mapNewAiCreate = async (data: any): Promise<Draft<IAIRecord>> => {
  // Extract from nested structure
  const org = data?.data?.org || {};
  const appForm = data?.data?.appForm || {};
  
  const {
    name,
    visitingAddress,
    shippingAddress,
    email,
    website,
  } = org;

  const nameShort = (name) ? (name.length < 50 ? name : name.slice(0, 50)) : '';
  const nameAbbr = (name) ? (name.length < 30 ? name : name.slice(0, 30)) : '';

  let minGrade: undefined | number = undefined;
  let maxGrade: undefined | number = undefined;

  // Note: new school code create form uses "studentsEnrolled" (with 's')
  enrollmentFields.forEach((map) => {
    const key = map.key;
    const field = map.field;
    const enrollment = appForm.studentsEnrolled || {};
    const value = enrollment[field];
    const count = parseInt(value);
    if (!isNaN(count) && count > 0) {
      if (minGrade === undefined) {
        minGrade = key;
      } else {
        maxGrade = key;
      }
    }
  });

  if (maxGrade === undefined) {
    maxGrade = minGrade;
  }

  // v3 uses different field names: streetAddr1, stateCd, zip5, countryIsoCd
  const mappedVisiting: Draft<IAddress> = {
    streetAddr1: visitingAddress?.streetAddr1,
    streetAddr2: visitingAddress?.streetAddr2 || undefined,
    streetAddr3: visitingAddress?.streetAddr3 || undefined,
    city: visitingAddress?.city,
    stateCd: visitingAddress?.stateCd,
    zip5: visitingAddress?.zip5,
    province: visitingAddress?.province || undefined,
    countryIsoCd: visitingAddress?.countryIsoCd,
    intlPostalCd: visitingAddress?.intlPostalCd || undefined,
  };

  // Check if shipping address exists and has required fields - province,intlPostalCd is optional for shipping
  const hasShippingAddress = shippingAddress && 
    shippingAddress.streetAddr1 && 
    shippingAddress.city && 
    shippingAddress.countryIsoCd;

  const mappedShipping: Draft<IAddress> | undefined = hasShippingAddress ? {
    streetAddr1: shippingAddress.streetAddr1,
    streetAddr2: shippingAddress.streetAddr2 || undefined,
    streetAddr3: shippingAddress.streetAddr3 || undefined,
    city: shippingAddress.city,
    stateCd: shippingAddress.stateCd,
    zip5: shippingAddress.zip5,
    province: shippingAddress.province || undefined,
    countryIsoCd: shippingAddress.countryIsoCd,
    intlPostalCd: shippingAddress.intlPostalCd || undefined,
    label: shippingAddress?.label || undefined,
    contactName: shippingAddress?.contactName || undefined,
    contactPhone: shippingAddress?.contactPhone || undefined
  } : undefined;

  return {
    org: {
      name: name,
      nameShort: nameShort,
      nameAbbr: nameAbbr,
      email: email,  // v3 uses 'email' not 'schoolEmail'
      website: website,  // v3 uses 'website' not 'webSite'
      visitingAddress: await mapAddress(mappedVisiting),
      shippingAddress: mappedShipping ? await mapAddress(mappedShipping) : undefined,
      charterInd: false,
      campus: false,
      lowestGradeCd: minGrade,
      highestGradeCd: maxGrade,
      phone: {}
    },
    ai: {

    }
  };
};
  const mapAiCreateHints = (form: TAIForm) => {
    const { phoneNumber } = form;
    return {
      org: {
        orgTypeCd: getTypes(form),
        phone: phoneNumber
      }
    };
  };

  const mapNewAiCreateHints = (form: any) => {
    const org = form?.data?.org || {};
    const phoneNumber = org.phone;
    
    return {
      org: {
        orgTypeCd: convertToOpsFormTypes(form),
        phone: phoneNumber
      }
    };
  };

  return {
    mapAiCreate,
    mapAiCreateHints,
    mapNewAiCreateHints,
    mapAiUpdate,
    mapAiaCreate,
    mapNewAiCreate
  };
};
